/**
 * GlobalChatNotifier — mounts once at the app root when the user is logged in
 * and keeps a persistent WebSocket open so members receive video-meeting
 * pings on ANY page (not just /chat).
 *
 * When a `meeting:notify` event arrives, we:
 *   1. Show a full "Incoming Video Call" modal with Join / Decline buttons.
 *   2. Loop a ringtone (/sounds/ringtone.wav) until the user acts.
 *   3. Trigger navigator.vibrate([500, 200, 500, 200, 500]) when supported.
 *   4. Also push a bell-badge event + browser Notification so background
 *      tabs/pages still get a visible signal.
 *   5. Auto-dismiss after 45 s so a missed call doesn't ring forever.
 *
 * Join → navigates to /chat/{cid} and opens the Daily meeting modal.
 * Decline → closes the modal and stops the ringtone/vibration.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Video, PhoneOff } from "lucide-react";
import { chatWsUrl } from "../pages/Chat";
import { useAuth } from "../context/AuthContext";

const RINGTONE_URL = "/sounds/ringtone.wav";
// How long to keep ringing before giving up. Mirrors a phone's missed-call
// timeout so a chat left idle doesn't jingle forever.
const AUTO_DISMISS_MS = 45000;
// Vibration cadence (ms on/off, repeated). Honors the user's device-level
// vibration settings — iOS Safari ignores this, which is fine.
const VIBRATION_PATTERN = [500, 200, 500, 200, 500];
// Re-fire the vibration pattern every N ms so the user keeps feeling the
// ring until they act (vibrate() plays once per call).
const VIBRATION_REPEAT_MS = 2500;

const NAVY = "#0A2463";
const RED = "#C8102E";

function ensureNotificationPermission() {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
    }
}

function fireBrowserNotification({ title, body, onClick }) {
    try {
        if (typeof Notification === "undefined") return;
        if (Notification.permission !== "granted") return;
        const n = new Notification(title, { body, icon: "/logo192.png", tag: "aop-incoming-call", requireInteraction: true });
        n.onclick = () => { window.focus(); onClick?.(); n.close(); };
    } catch { /* ignore */ }
}

export default function GlobalChatNotifier() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const audioRef = useRef(null);
    const vibrationIntervalRef = useRef(null);
    const autoDismissTimeoutRef = useRef(null);
    const [incoming, setIncoming] = useState(null); // { conversation_id, conversation_name, started_by_name, message_id, meeting_url }

    // ---- Ringtone + vibration lifecycle ----
    const stopRinging = useCallback(() => {
        try {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
        } catch { /* noop */ }
        if (vibrationIntervalRef.current) {
            clearInterval(vibrationIntervalRef.current);
            vibrationIntervalRef.current = null;
        }
        try { navigator.vibrate?.(0); } catch { /* noop */ }
        if (autoDismissTimeoutRef.current) {
            clearTimeout(autoDismissTimeoutRef.current);
            autoDismissTimeoutRef.current = null;
        }
    }, []);

    const startRinging = useCallback(() => {
        try {
            if (!audioRef.current) {
                audioRef.current = new Audio(RINGTONE_URL);
                audioRef.current.loop = true;
                audioRef.current.preload = "auto";
            }
            audioRef.current.currentTime = 0;
            const p = audioRef.current.play();
            if (p?.catch) p.catch(() => { /* autoplay blocked — ignore */ });
        } catch { /* ignore */ }
        try {
            if (navigator.vibrate) {
                navigator.vibrate(VIBRATION_PATTERN);
                // vibrate() runs the pattern once; re-fire it periodically so
                // the device keeps buzzing until the user picks up.
                vibrationIntervalRef.current = setInterval(() => {
                    try { navigator.vibrate?.(VIBRATION_PATTERN); } catch { /* noop */ }
                }, VIBRATION_REPEAT_MS);
            }
        } catch { /* ignore */ }
    }, []);

    const dismiss = useCallback(() => {
        stopRinging();
        setIncoming(null);
    }, [stopRinging]);

    const accept = useCallback(() => {
        const target = incoming;
        stopRinging();
        setIncoming(null);
        if (!target) return;
        // Navigate to the chat; the chat page auto-opens an in-progress
        // meeting card. Query param tells Chat.jsx which meeting to open.
        navigate(`/chat/${target.conversation_id}?join=${encodeURIComponent(target.message_id)}`);
    }, [incoming, stopRinging, navigate]);

    // ---- WebSocket connection ----
    useEffect(() => {
        if (!user) return undefined;

        ensureNotificationPermission();

        let stopped = false;
        function connect() {
            if (stopped) return;
            let ws;
            try { ws = new WebSocket(chatWsUrl()); } catch { scheduleReconnect(); return; }
            wsRef.current = ws;
            ws.onmessage = (ev) => {
                let msg;
                try { msg = JSON.parse(ev.data); } catch { return; }
                if (msg?.type !== "meeting:notify") return;

                // Bell badge for any listener.
                window.dispatchEvent(new CustomEvent("aop:meeting_ping", { detail: msg }));

                // Show the ringing modal + play ringtone + vibrate.
                setIncoming({
                    conversation_id: msg.conversation_id,
                    conversation_name: msg.conversation_name,
                    started_by_name: msg.started_by_name,
                    message_id: msg.message_id,
                    meeting_url: msg.meeting_url,
                });

                // OS-level notification for background tabs — tapping it
                // brings focus and auto-joins via the same deep link.
                fireBrowserNotification({
                    title: "Incoming Video Call",
                    body: `${msg.conversation_name} — ${msg.started_by_name} is calling`,
                    onClick: () => navigate(`/chat/${msg.conversation_id}?join=${encodeURIComponent(msg.message_id)}`),
                });
            };
            ws.onclose = () => {
                if (stopped) return;
                scheduleReconnect();
            };
            ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
        }

        function scheduleReconnect() {
            if (reconnectTimeoutRef.current) return;
            reconnectTimeoutRef.current = setTimeout(() => {
                reconnectTimeoutRef.current = null;
                connect();
            }, 3000);
        }

        connect();
        return () => {
            stopped = true;
            if (reconnectTimeoutRef.current) { clearTimeout(reconnectTimeoutRef.current); reconnectTimeoutRef.current = null; }
            try { wsRef.current?.close(); } catch { /* noop */ }
        };
    }, [user, navigate]);

    // ---- Start/stop the ring when `incoming` changes ----
    useEffect(() => {
        if (!incoming) return undefined;
        startRinging();
        autoDismissTimeoutRef.current = setTimeout(() => {
            // Treat as missed call — stop ringing but leave the "missed"
            // bell badge in place.
            stopRinging();
            setIncoming(null);
        }, AUTO_DISMISS_MS);
        return () => { stopRinging(); };
    }, [incoming, startRinging, stopRinging]);

    // Clean up on sign-out / unmount.
    useEffect(() => () => stopRinging(), [stopRinging]);

    if (!incoming) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm px-4"
            data-testid="incoming-call-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="incoming-call-title"
        >
            <div
                className="w-full max-w-sm rounded-3xl bg-white shadow-2xl overflow-hidden border-2"
                style={{ borderColor: NAVY }}
            >
                <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #152E6B 100%)`, color: "#fff" }}>
                    <div className="text-[11px] uppercase tracking-[0.18em] opacity-80 mb-1">Incoming video call</div>
                    <div id="incoming-call-title" className="font-heading font-bold text-2xl leading-tight mb-1 line-clamp-2">
                        {incoming.conversation_name || "Group chat"}
                    </div>
                    <div className="text-sm opacity-90 mb-5">{incoming.started_by_name || "A member"} is calling…</div>
                    <div className="relative h-20 w-20 rounded-full grid place-items-center mb-2" style={{ backgroundColor: "rgba(255,255,255,0.12)" }}>
                        <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ backgroundColor: "rgba(255,255,255,0.4)" }} />
                        <Video className="h-8 w-8 relative" />
                    </div>
                </div>
                <div className="px-6 py-5 flex items-center justify-around gap-4">
                    <button
                        type="button"
                        onClick={dismiss}
                        className="flex flex-col items-center gap-2 group"
                        data-testid="incoming-call-decline-btn"
                        aria-label="Decline call"
                    >
                        <span
                            className="h-14 w-14 rounded-full grid place-items-center text-white shadow-lg transition-transform group-active:scale-95 group-hover:scale-105"
                            style={{ backgroundColor: RED }}
                        >
                            <PhoneOff className="h-6 w-6" />
                        </span>
                        <span className="text-xs font-semibold text-slate-700">Decline</span>
                    </button>
                    <button
                        type="button"
                        onClick={accept}
                        className="flex flex-col items-center gap-2 group"
                        data-testid="incoming-call-join-btn"
                        aria-label="Join call"
                    >
                        <span
                            className="h-14 w-14 rounded-full grid place-items-center text-white shadow-lg transition-transform group-active:scale-95 group-hover:scale-105"
                            style={{ backgroundColor: "#16a34a" }}
                        >
                            <Video className="h-6 w-6" />
                        </span>
                        <span className="text-xs font-semibold text-slate-700">Join</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
